/**
 * @fileoverview Login page for user authentication with email and OTP
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { LandingNavbar } from '@/components/layout/LandingNavbar';
import { apiService } from '@/services/api/apiService';
import { otpService } from '@/services/auth/otpService';
import { documentService } from '@/services/documents/documentService';
import { documentAccessService } from '@/services/documents/documentAccessService';

// Initialize mock documents on page load
if (typeof window !== 'undefined') {
  documentService.initializeMockDocuments();
}


export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState('email'); // 'email' or '2fa'
  const [otpToken, setOtpToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Clear any existing session to prevent identity confusion
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');

      if (step === 'email') {
        if (!email || !email.includes('@')) {
          setError(t('auth.invalid_email', 'Por favor, ingrese un email válido'));
          setIsLoading(false);
          return;
        }

        // Logic for backend login (Stage 1)
        const data = await apiService.request('/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });

        if (data.twoFactorRequired) {
          sessionStorage.setItem('pendingLoginEmail', email);
          navigate('/otp');
        } else {
          // Direct login if 2FA not enabled
          login(data.user, data.token);

          // Role-based navigation
          const userRole = data.user.role || data.user.roles?.[0]?.role_name || 'certifier';
          if (userRole === 'student' || userRole === 'recipient') {
            navigate('/wallet');
          } else {
            navigate('/courses');
          }
        }
      } else {
        // Step: 2fa
        const data = await apiService.request('/auth/verify-2fa', {
          method: 'POST',
          body: JSON.stringify({ email, token: otpToken }),
        });

        login(data.user, data.token);

        // Role-based navigation
        const userRole = data.user.role || data.user.roles?.[0]?.role_name || 'certifier';
        if (userRole === 'student' || userRole === 'recipient') {
          navigate('/wallet');
        } else {
          navigate('/courses');
        }
      }
    } catch (err) {
      // Handle different error types
      if (err.message && err.message.includes('fetch')) {
        // Actual connection/network error
        setError(t('auth.conn_error', 'Error de conexión con el servidor'));
      } else if (err.status === 401 || err.message === 'Invalid email or password') {
        // Authentication failed - wrong credentials
        setError(t('auth.invalid_credentials', 'Credenciales incorrectas. Por favor, verifica tu email y contraseña.'));
      } else if (err.status === 403) {
        // Account inactive
        setError(t('auth.account_inactive', 'Tu cuenta está inactiva. Contacta al administrador.'));
      } else {
        // Other errors
        setError(err.message || t('auth.login_error', 'Error al iniciar sesión. Por favor, intenta nuevamente.'));
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <LandingNavbar />

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Columna Izquierda - Información (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 bg-[#192A42] text-white p-12 xl:p-24 flex-col justify-center relative overflow-hidden">
          {/* Background Patterns */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[url('/patterns/cubes.png')] opacity-[0.05]"></div>
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight text-white">
              {t('landing.hero_title_1', 'Certificación Digital')} <br />
              <span className="text-white">{t('landing.hero_title_2', 'Segura y Confiable')}</span>
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed max-w-lg">
              {t('landing.hero_subtitle', 'Plataforma digital genérica para emitir y gestionar certificados y documentos con tecnología blockchain, garantizando autenticidad e inmutabilidad.')}
            </p>

            <ul className="space-y-6 pt-4">
              {[
                { text: t('landing.stat_blockchain', 'Certificados inmutables en blockchain'), icon: 'shield-alt' },
                { text: t('landing.stat_qr', 'Validación instantánea QR'), icon: 'qrcode' },
                { text: t('landing.stat_digital', 'Gestión 100% digital'), icon: 'file-contract' }
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-4 bg-white/5 p-4 rounded-xl backdrop-blur-sm border border-white/10">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                    <i className={`fas fa-${item.icon}`}></i>
                  </div>
                  <span className="font-medium">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Columna Derecha - Formulario */}
        <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-12 lg:w-1/2 bg-white">
          <div className="w-full max-w-md space-y-8">


            <div className="text-center lg:text-left space-y-2">
              <h1 className="text-3xl font-extrabold text-slate-900">
                {step === 'email' ? t('auth.login_title', 'Iniciar Sesión') : t('auth.2fa_title', 'Verificación 2FA')}
              </h1>
              <p className="text-slate-500">
                {step === 'email'
                  ? t('auth.login_subtitle', 'Ingresa tu email para continuar')
                  : t('auth.2fa_subtitle', 'Ingresa el código de 6 dígitos de Google Authenticator')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 mt-8">
              {step === 'email' ? (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 ml-1">
                      {t('auth.email', 'Correo Electrónico')}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none placeholder:text-slate-400"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.email_placeholder', 'nombre@empresa.com')}
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 ml-1">
                      {t('auth.password', 'Contraseña')}
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none placeholder:text-slate-400"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                        <i className="fas fa-lock text-lg"></i>
                      </div>
                    </div>
                    <div className="flex justify-end mt-1">
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {t('auth.forgot_password', '¿Olvidaste tu contraseña?')}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 ml-1">
                    {t('auth.security_code', 'Código de Seguridad')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      maxLength="6"
                      className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-3xl font-bold tracking-[0.5em] text-center bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none placeholder:text-slate-300"
                      value={otpToken}
                      onChange={(e) => setOtpToken(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      required
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1.5 ml-1 mt-2">
                    <i className="fas fa-mobile-alt text-blue-500"></i>
                    {t('auth.2fa_open_app', 'Abre la app de Google Authenticator para ver el código')}
                  </p>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 flex items-start gap-3 animate-fade-in-up">
                  <i className="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold text-lg shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
                disabled={isLoading || (step === 'email' ? (!email || !password) : otpToken.length < 6)}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>
                    {step === 'email' ? t('common.verifying', 'Verificando...') : t('common.validating', 'Validando...')}
                  </>
                ) : (
                  <>
                    <span>{step === 'email' ? t('common.continue', 'Continuar') : t('auth.verify_enter', 'Verificar e Ingresar')}</span>
                    <i className="fas fa-arrow-right"></i>
                  </>
                )}
              </button>

              {step === '2fa' && !isLoading && (
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="w-full text-slate-500 text-sm font-medium hover:text-slate-700 transition-colors"
                >
                  {t('auth.change_email', 'Cambiar correo')}
                </button>
              )}
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

